import React from "react";

/*
  Shared "teal header band + tiles" building blocks. Layout + colour only.
  Reused across every signed-in screen so they stay consistent.
*/

const TEAL_BAND = "linear-gradient(135deg,#0F6B6B,#0A4A4A)";
const ORANGE = "linear-gradient(135deg,#D9774A,#E9A94B)";

const CHIP = {
  green: "bg-[#E3F1E9] text-[#1D6B4A]",
  blue: "bg-[#E1EDF7] text-[#1F5C8F]",
  yellow: "bg-[#FBF4DD] text-[#8A6B1E]",
  red: "bg-[#F9E4DF] text-[#A8372A]",
  grey: "bg-[#EEE9E0] text-[#6B635C]",
};

// Full-bleed teal band pinned to the top of the content area. `overlap` adds
// bottom padding so the tile row can sit over its lower edge.
export function PageBand({ title, right, children, overlap = false, flushPhone = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`-mt-6 md:-mt-8 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 text-white overflow-x-hidden${flushPhone ? " tm-band-flush-phone" : ""}`}
      style={{ background: TEAL_BAND, paddingTop: 22, ...(flushPhone ? {} : { paddingBottom: overlap ? 36 : 22 }) }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1 basis-full sm:basis-auto">
          {typeof title === "string" ? (
            <h1 className="font-bold leading-tight" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              {title}
            </h1>
          ) : (
            title
          )}
        </div>
        {right ? <div className="min-w-0 max-w-full w-full sm:w-auto shrink-0">{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

// Big money figure for inside the band.
export function BandBigNumber({ value, label, testid, valueColor }) {
  return (
    <div className="mt-3" data-testid={testid}>
      <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em", color: valueColor }}>{value}</div>
      <div style={{ fontSize: 13, color: "#D3E8E6" }} className="mt-1">{label}</div>
    </div>
  );
}

// Wrapper that lifts the tile grid to overlap the band above it.
export function TileRow({ children, cols = "grid-cols-2", className = "", flushPhone = false }) {
  // Inline marginTop so the 34px overlap wins over any `space-y-*` margin on
  // the parent (utility specificity would otherwise override a -mt class).
  // When flushPhone is set, the tm-tiles-flush-phone class (with !important)
  // drives the margin so phone sits below the band and tablet/desktop overlap.
  return (
    <div
      className={`grid ${cols} gap-3 relative ${className}${flushPhone ? " tm-tiles-flush-phone" : ""}`}
      style={flushPhone ? undefined : { marginTop: -34 }}
    >
      {children}
    </div>
  );
}

export function StatTile({ label, value, hint, tone = "text-[#2A2623]", bg = "#FFFFFF", accent, big = false, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`border border-[#E8E1D6] ${className}`}
      style={{
        borderRadius: 18,
        padding: 14,
        boxShadow: "0 8px 18px rgba(42,38,35,0.08)",
        background: bg,
        borderLeft: accent ? `4px solid ${accent}` : undefined,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600 }} className="text-[#6B635C]">{label}</div>
      <div style={{ fontSize: big ? 32 : 24, fontWeight: 800 }} className={`${tone} mt-1 leading-none`}>{value}</div>
      {hint ? <div style={{ fontSize: 11 }} className="text-[#6B635C] mt-1.5">{hint}</div> : null}
    </div>
  );
}

export function StatusChip({ variant = "grey", children, className = "" }) {
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded whitespace-nowrap ${CHIP[variant]} ${className}`}>
      {children}
    </span>
  );
}

// White list row card with an optional coloured left accent bar.
export function ListRowCard({ accent, className = "", style = {}, children, ...rest }) {
  return (
    <div
      className={`bg-white border border-[#E8E1D6] flex items-center gap-3 ${className}`}
      style={{ borderRadius: 16, padding: 14, borderLeft: accent ? `5px solid ${accent}` : undefined, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

// The single orange primary button per screen.
export function OrangeButton({ children, className = "", style = {}, ...rest }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 font-extrabold ${className}`}
      style={{ background: ORANGE, color: "#2A2623", minHeight: 44, borderRadius: 12, padding: "0 18px", fontWeight: 800, ...style }}
      {...rest}
    >
      {children}
    </button>
  );
}

export { TEAL_BAND, ORANGE, CHIP };
